import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  IconButton,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Facebook as FacebookIcon,
  MusicNote as TikTokIcon,
  CameraAlt as SnapchatIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';

const ConnectMoreModal = ({ open, onClose, connectedAccounts = [] }) => {
  const { connectSocialAccount } = useAuth();

  const socialPlatforms = [
    {
      name: 'Instagram',
      icon: <InstagramIcon sx={{ fontSize: 40, color: '#E4405F' }} />,
      description: 'Track your Instagram posts, followers, and engagement',
      color: '#E4405F',
      available: true
    },
    {
      name: 'Facebook',
      icon: <FacebookIcon sx={{ fontSize: 40, color: '#1877F2' }} />,
      description: 'Monitor your Facebook pages and post performance',
      color: '#1877F2',
      available: true
    },
    {
      name: 'YouTube',
      icon: <YouTubeIcon sx={{ fontSize: 40, color: '#FF0000' }} />,
      description: 'Analyze your YouTube channel and video analytics',
      color: '#FF0000',
      available: true
    },
    {
      name: 'TikTok',
      icon: <TikTokIcon sx={{ fontSize: 40, color: '#000000' }} />,
      description: 'Track your TikTok videos and performance metrics',
      color: '#000000',
      available: true
    },
    {
      name: 'Snapchat',
      icon: <SnapchatIcon sx={{ fontSize: 40, color: '#FFFC00' }} />,
      description: 'Connect your Snapchat account for insights',
      color: '#FFFC00',
      available: true
    }
  ];

  const isPlatformConnected = (platformName) => {
    return connectedAccounts.some(account => 
      account.platform.toLowerCase() === platformName.toLowerCase()
    );
  };

  const handleConnect = (platform) => {
    connectSocialAccount(platform.toLowerCase());
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="div">
            Connect Social Media Account
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body1" color="text.secondary" paragraph>
          Choose a social media platform to connect and start analyzing your data
        </Typography>
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {socialPlatforms.map((platform) => {
            const isConnected = isPlatformConnected(platform.name);
            return (
              <Grid item xs={12} sm={6} key={platform.name}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.2s',
                    opacity: isConnected ? 0.7 : 1,
                    '&:hover': !isConnected ? {
                      transform: 'translateY(-2px)',
                      boxShadow: 3
                    } : {}
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 2 }}>
                    <Box mb={2} position="relative">
                      {platform.icon}
                      {isConnected && (
                        <Chip
                          icon={<CheckIcon />}
                          label="Connected"
                          color="success"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom>
                      {platform.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {platform.description}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Button
                      variant={isConnected ? "outlined" : "contained"}
                      size="small"
                      onClick={() => handleConnect(platform.name)}
                      disabled={!platform.available || isConnected}
                      startIcon={isConnected ? <CheckIcon /> : null}
                      sx={{
                        backgroundColor: isConnected ? 'transparent' : platform.color,
                        color: isConnected ? 'text.secondary' : 'white',
                        borderColor: isConnected ? 'text.secondary' : 'transparent',
                        '&:hover': isConnected ? {
                          backgroundColor: 'transparent'
                        } : {
                          backgroundColor: platform.color,
                          opacity: 0.9
                        }
                      }}
                    >
                      {isConnected ? 'Connected' : `Connect ${platform.name}`}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectMoreModal;
